import {
  COURT_NAMES,
  COURT_ORDER,
  LEGAL_AREAS,
  type CatalogueCase,
  type LegalArea,
} from '@buakei/types';
import { EmptyState, ErrorState, LoadingState, Notice } from '@buakei/ui';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ExternalLink, Gavel, Scale } from 'lucide-react';
import { useEffect, useId, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import type { IdentityClient } from '@/auth/identity-client';
import { CaseBriefPanel } from '@/assistant/CaseBriefPanel';
import { libraryApi } from '@/services/api';
import { ReportProblem } from '@/assistant/ReportProblem';
import { Page } from '@/shell/Page';
import {
  BackLinks,
  Denied,
  LibraryTabs,
  OriginalReader,
  ProvisionalNotice,
  SourceVerdicts,
  useReaderTokens,
} from './LibraryPages';

/**
 * Judgments in the library (ADR-018), from SierraLII. Each case shows the original PDF and
 * credits SierraLII with a link to the judgment's page there, as its licence requires.
 */

const RESULTS_LIMIT = 100;

const courtName = (code: string) => COURT_NAMES[code] ?? code;

export function useCases(tokens: IdentityClient | null) {
  return useQuery({
    queryKey: ['library', 'cases'],
    queryFn: () => libraryApi.cases(tokens!),
    enabled: tokens !== null,
    refetchInterval: 60_000,
  });
}

function formatDate(iso: string | null) {
  if (!iso) return null;
  const date = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(date.getTime())
    ? iso
    : date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
      });
}

/** The case name without the citation and date SierraLII appends to its titles. */
export function caseName(c: CatalogueCase): string {
  return c.title.replace(/\s*\[\d{4}\]\s+[A-Za-z]+\s+\d+\s*\([^)]*\)\s*$/, '').trim() || c.title;
}

function SierraLiiCredit({ url }: { url: string | null }) {
  return (
    <p className="text-sm text-muted-600">
      Source: SierraLII (sierralii.gov.sl), used under its Creative Commons
      Attribution-NonCommercial licence.{' '}
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noreferrer noopener"
          className="inline-flex items-center gap-1 font-medium text-navy-900 underline"
        >
          View on SierraLII <ExternalLink className="size-3.5" aria-hidden />
          <span className="sr-only">(opens in a new tab)</span>
        </a>
      ) : null}
    </p>
  );
}

/** Wait for typing to pause before searching the judgments' text. */
function useSettled(value: string, ms = 400): string {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return settled;
}

/**
 * Judgments whose text discusses the search words (ADR-034), best match first, each with the
 * passage where it matched. `pick` chooses a case in place (Case briefs); otherwise each
 * result opens the judgment at that page.
 */
export function CaseTextResults({
  tokens,
  query,
  cases,
  area,
  exclude,
  pick,
}: {
  tokens: IdentityClient;
  query: string;
  cases: CatalogueCase[];
  area: string;
  exclude: Set<string>;
  pick?: (versionId: string) => void;
}) {
  const settled = useSettled(query.trim());
  const search = useQuery({
    queryKey: ['library', 'case-search', settled],
    queryFn: () => libraryApi.searchCases(tokens, settled),
    enabled: settled.length >= 3,
    staleTime: 5 * 60_000,
  });
  if (settled.length < 3) return null;
  const byId = new Map(cases.map((c) => [c.versionId, c]));
  const hits = (search.data?.hits ?? []).filter((h) => {
    const c = byId.get(h.versionId);
    return !exclude.has(h.versionId) && (!area || Boolean(c?.areas?.includes(area)));
  });
  return (
    <section aria-label="Judgments that discuss your search" className="flex flex-col gap-3">
      <h2 className="text-sm font-semibold text-navy-900">Judgments that discuss “{settled}”</h2>
      {search.isPending ? <LoadingState label="Searching the judgments" /> : null}
      {search.isError ? (
        <ErrorState
          title="The judgments could not be searched"
          onRetry={() => void search.refetch()}
        >
          {search.error.message}
        </ErrorState>
      ) : null}
      {search.data && hits.length === 0 ? (
        <p className="text-sm text-muted-600">
          Not found in the text of the judgments in the library. Try other words.
        </p>
      ) : null}
      <ul className="flex flex-col divide-y divide-line-200 rounded-lg border border-line-200 bg-white">
        {hits.map((h) => {
          const c = byId.get(h.versionId);
          const name = c ? caseName(c) : h.title;
          const body = (
            <>
              <span className="font-medium text-navy-900">{name}</span>
              <span className="text-xs text-muted-600">
                {[c?.citation, c ? courtName(c.court) : null, h.label].filter(Boolean).join(' · ')}
              </span>
              <span className="text-sm text-ink-900">{h.excerpt}</span>
            </>
          );
          return (
            <li key={h.versionId}>
              {pick ? (
                <button
                  type="button"
                  onClick={() => pick(h.versionId)}
                  className="flex w-full flex-col gap-1 p-3 text-left hover:bg-navy-50"
                >
                  {body}
                </button>
              ) : (
                <Link
                  to={`/library/cases/${h.versionId}?page=${h.pageStart}`}
                  className="flex flex-col gap-1 p-3 hover:bg-navy-50"
                >
                  {body}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** A legal area's name, for a stored area id (ADR-033). */
export const areaName = (id: string) => LEGAL_AREAS[id as LegalArea] ?? id;

/** What a case search looks through: parties, citations, and suggested issues and areas. */
export function caseHaystack(c: CatalogueCase): string {
  return [c.title, ...c.aliases, ...(c.issues ?? []), ...(c.areas ?? []).map(areaName)]
    .join(' ')
    .toLowerCase();
}

/** Choose a legal area; only areas that some judgment carries are offered, with counts. */
export function AreaFilter({
  cases,
  value,
  onChange,
}: {
  cases: CatalogueCase[];
  value: string;
  onChange: (area: string) => void;
}) {
  const id = useId();
  const counts = new Map<string, number>();
  for (const c of cases) for (const a of c.areas ?? []) counts.set(a, (counts.get(a) ?? 0) + 1);
  if (counts.size === 0) return null;
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-medium text-ink-900">
        Legal area
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 rounded-md border border-line-200 bg-white px-2 text-ink-900"
      >
        <option value="">All areas</option>
        {(Object.keys(LEGAL_AREAS) as LegalArea[])
          .filter((a) => counts.has(a))
          .map((a) => (
            <option key={a} value={a}>
              {LEGAL_AREAS[a]} ({counts.get(a)})
            </option>
          ))}
      </select>
    </div>
  );
}

/** The suggested areas and issues of one judgment, marked as not reviewed (ADR-033). */
export function CaseLabels({ c, compact = false }: { c: CatalogueCase; compact?: boolean }) {
  if (!c.issues?.length && !c.areas?.length) return null;
  if (compact) {
    return <span className="text-xs text-muted-600">{(c.issues ?? []).join(' · ')}</span>;
  }
  return (
    <section aria-label="Suggested areas and issues" className="flex flex-col gap-1 text-sm">
      <p className="text-ink-900">
        <span className="font-medium text-navy-900">Areas: </span>
        {(c.areas ?? []).map(areaName).join('; ')}
      </p>
      <p className="text-ink-900">
        <span className="font-medium text-navy-900">Issues: </span>
        {(c.issues ?? []).join('; ')}
      </p>
      <p className="text-xs text-muted-600">
        Suggested automatically from the judgment's text to help you find cases; not reviewed by an
        editor. Read the judgment for what it decides.
      </p>
    </section>
  );
}

function CaseList({ cases }: { cases: CatalogueCase[] }) {
  return (
    <ul className="grid gap-3 md:grid-cols-2">
      {cases.map((c) => (
        <li key={c.versionId}>
          <Link
            to={`/library/cases/${c.versionId}`}
            className="flex h-full items-start gap-3 rounded-xl border border-line-200 bg-white p-4 hover:border-navy-900 hover:bg-navy-50"
          >
            <Gavel className="mt-0.5 size-5 shrink-0 text-navy-900" aria-hidden />
            <span className="flex flex-col gap-1">
              <span className="font-medium text-navy-900">{caseName(c)}</span>
              <span className="text-sm text-muted-600">
                {[c.citation, formatDate(c.decisionDate), 'PDF'].filter(Boolean).join(' · ')}
              </span>
              <CaseLabels c={c} compact />
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

export function CasesPage() {
  const tokens = useReaderTokens();
  const cases = useCases(tokens);
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const searchId = useId();
  const court = params.get('court');
  const area = params.get('area') ?? '';
  const setArea = (next: string) =>
    setParams((current) => {
      const p = new URLSearchParams(current);
      if (next) p.set('area', next);
      else p.delete('area');
      return p;
    });

  const everything = useMemo(() => cases.data?.cases ?? [], [cases.data]);
  const all = useMemo(
    () => (area ? everything.filter((c) => c.areas?.includes(area)) : everything),
    [everything, area],
  );
  const courts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const c of all) counts.set(c.court, (counts.get(c.court) ?? 0) + 1);
    return [...counts].sort(
      ([a], [b]) => (COURT_ORDER.indexOf(a) + 1 || 99) - (COURT_ORDER.indexOf(b) + 1 || 99),
    );
  }, [all]);
  const matches = useMemo(() => {
    const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (terms.length === 0) return null;
    return all.filter((c) => {
      const haystack = caseHaystack(c);
      return terms.every((t) => haystack.includes(t));
    });
  }, [all, query]);

  if (!tokens) return <Denied />;
  const inCourt = court ? all.filter((c) => c.court === court) : [];

  return (
    <Page
      title="Library"
      description="Acts and judgments of Sierra Leone, as published."
      width="wide"
    >
      <LibraryTabs current="cases" />
      <ProvisionalNotice />
      <SierraLiiCredit url={null} />
      {cases.isPending ? <LoadingState label="Loading the cases" /> : null}
      {cases.isError ? (
        <ErrorState title="The cases could not be loaded" onRetry={() => void cases.refetch()}>
          {cases.error.message}
        </ErrorState>
      ) : null}
      {cases.data ? (
        <>
          <div className="flex flex-col gap-1">
            <label htmlFor={searchId} className="text-sm font-medium text-ink-900">
              Search by party, case number, citation or issue
            </label>
            <input
              id={searchId}
              type="search"
              value={query}
              placeholder="e.g. Kamara, CIV APP 3, [1962] SLCA 1, title to land"
              onChange={(e) => setQuery(e.target.value)}
              className="h-10 rounded-md border border-line-200 bg-white px-3 text-ink-900 placeholder:text-muted-600"
            />
          </div>
          <AreaFilter cases={everything} value={area} onChange={setArea} />

          {matches ? (
            <div className="flex flex-col gap-6">
              {matches.length > 0 ? (
                <section aria-label="Search results" className="flex flex-col gap-3">
                  <p className="text-sm text-ink-900" role="status" aria-live="polite">
                    {matches.length > RESULTS_LIMIT
                      ? `Showing the first ${RESULTS_LIMIT} of ${matches.length} cases matching by name, citation or issue`
                      : `${matches.length} ${matches.length === 1 ? 'case matches' : 'cases match'} by name, citation or issue`}
                  </p>
                  <CaseList cases={matches.slice(0, RESULTS_LIMIT)} />
                </section>
              ) : null}
              <CaseTextResults
                tokens={tokens}
                query={query}
                cases={everything}
                area={area}
                exclude={new Set(matches.slice(0, RESULTS_LIMIT).map((c) => c.versionId))}
              />
            </div>
          ) : area && !court ? (
            <section aria-label="Judgments in this area" className="flex flex-col gap-3">
              <p className="text-sm text-ink-900" role="status" aria-live="polite">
                {all.length} judgments in {areaName(area)}
                {all.length > RESULTS_LIMIT ? `; showing the first ${RESULTS_LIMIT}` : ''}
              </p>
              <CaseList
                cases={[...all]
                  .sort(
                    (a, b) =>
                      (COURT_ORDER.indexOf(a.court) + 1 || 99) -
                        (COURT_ORDER.indexOf(b.court) + 1 || 99) ||
                      (b.decisionDate ?? '').localeCompare(a.decisionDate ?? ''),
                  )
                  .slice(0, RESULTS_LIMIT)}
              />
            </section>
          ) : court ? (
            <section aria-labelledby="court-heading" className="flex flex-col gap-3">
              <button
                type="button"
                onClick={() => setParams({})}
                className="flex items-center gap-1 self-start text-sm font-medium text-navy-900 underline"
              >
                <ArrowLeft className="size-4" aria-hidden /> All courts
              </button>
              <h2 id="court-heading" className="font-display text-2xl text-navy-900">
                {courtName(court)} · {inCourt.length} judgments
              </h2>
              {inCourt.length === 0 ? (
                <EmptyState title="No judgments for this court">Choose another court.</EmptyState>
              ) : (
                <CaseList cases={inCourt} />
              )}
            </section>
          ) : courts.length === 0 ? (
            <EmptyState title="No cases yet">
              Judgments appear here as they are uploaded.
            </EmptyState>
          ) : (
            <>
              <p className="text-sm text-ink-900" role="status" aria-live="polite">
                {all.length} judgments from {courts.length} courts
              </p>
              <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {courts.map(([code, count]) => (
                  <li key={code}>
                    <Link
                      to={`?court=${code}`}
                      className="flex h-full items-start gap-3 rounded-xl border border-line-200 bg-white p-4 hover:border-navy-900 hover:bg-navy-50"
                    >
                      <Scale className="mt-1 size-5 shrink-0 text-navy-900" aria-hidden />
                      <span className="flex flex-col gap-1">
                        <span className="font-display text-lg text-navy-900">
                          {courtName(code)}
                        </span>
                        <span className="text-sm text-muted-600">
                          {count} {count === 1 ? 'judgment' : 'judgments'}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      ) : null}
    </Page>
  );
}

export function CasePage() {
  const tokens = useReaderTokens();
  const { versionId = '' } = useParams();
  const cases = useCases(tokens);
  if (!tokens) return <Denied />;
  const found = cases.data?.cases.find((c) => c.versionId === versionId);
  return (
    <Page title={found ? caseName(found) : 'Case'} width="wide">
      <BackLinks
        fallback={found ? `/library/cases?court=${found.court}` : '/library/cases'}
        fallbackLabel="Back to the cases"
      />
      {cases.isPending ? <LoadingState label="Loading the case" /> : null}
      {cases.isError ? (
        <ErrorState title="This case could not be loaded" onRetry={() => void cases.refetch()}>
          {cases.error.message}
        </ErrorState>
      ) : null}
      {cases.data && !found ? (
        <EmptyState title="Case not found">
          It is not in the library available to you. It may not have been uploaded yet.
        </EmptyState>
      ) : null}
      {found ? (
        <>
          <dl className="grid gap-x-8 gap-y-1 text-sm sm:grid-cols-[max-content_1fr]">
            <dt className="text-muted-600">Court</dt>
            <dd className="text-ink-900">{courtName(found.court)}</dd>
            {found.citation ? (
              <>
                <dt className="text-muted-600">Citation</dt>
                <dd className="text-ink-900">
                  {[found.citation, ...found.aliases].join('; also published as ')}
                </dd>
              </>
            ) : null}
            {found.decisionDate ? (
              <>
                <dt className="text-muted-600">Judgment date</dt>
                <dd className="text-ink-900">{formatDate(found.decisionDate)}</dd>
              </>
            ) : null}
          </dl>
          <Notice title="Provisional and unverified" icon={Gavel}>
            This judgment is shown as published by SierraLII. Its later treatment (appeal,
            overruling) has not been checked.
          </Notice>
          <SierraLiiCredit url={found.url} />
          <CaseLabels c={found} />
          <SourceVerdicts tokens={tokens} versionId={found.versionId} />
          <ReportProblem versionId={found.versionId} context="library" />
          <CaseBriefPanel tokens={tokens} versionId={found.versionId} />
          <OriginalReader tokens={tokens} doc={found} />
        </>
      ) : null}
    </Page>
  );
}
